import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import Modal from "../ui/Modal";
import ConfirmDialog from "../ui/ConfirmDialog";
import { ESCOPO_CATEGORIA, ESCOPO_LABEL } from "../../services/categoriasService";

// Sub-modal genérico de CRUD de taxonomia (Categoria ou FormaPagamento).
// Listar + criar + editar + excluir embutidos. Itens de sistema (IsSistema)
// têm Nome editável mas Escopo (se aplicável) e exclusão bloqueados.
//
// Props:
//   open, onClose          — controle do modal
//   onList                 — () => Promise<Item[]>
//   onCreate               — (payload) => Promise<Item>  (payload: {nome, escopo?})
//   onUpdate               — (id, payload) => Promise<void>
//   onDelete               — (id) => Promise<void>
//   titulo                 — ex.: "Gerenciar Categorias"
//   itemLabel              — ex.: "categoria" (usado em mensagens: "Excluir categoria?")
//   hasEscopo              — true só pra Categoria (mostra dropdown Escopo)
//   onItemCreated          — callback (item) chamado após criar; pai pré-seleciona
//   onListChanged          — callback () chamado após qualquer mutação (criar/editar/excluir)
//
// Padrão visual herda o <Modal> UI base (z-index 1000 — quando aberto em cima
// de outro Modal, last-mounted-wins no DOM, fica visualmente em cima).
export default function TaxonomyManagerModal({
  open,
  onClose,
  onList,
  onCreate,
  onUpdate,
  onDelete,
  titulo,
  itemLabel = "item",
  hasEscopo = false,
  onItemCreated,
  onListChanged,
}) {
  const { C } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Formulário de criação (topo)
  const [novoNome, setNovoNome] = useState("");
  const [novoEscopo, setNovoEscopo] = useState(ESCOPO_CATEGORIA.Despesa);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Edição inline de um item existente
  const [editingId, setEditingId] = useState(null);
  const [editingNome, setEditingNome] = useState("");
  const [editingEscopo, setEditingEscopo] = useState(ESCOPO_CATEGORIA.Despesa);
  const [editingError, setEditingError] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Confirmação de exclusão
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const list = await onList();
      setItems(list);
      setError(null);
    } catch (err) {
      // Log no console preserva a mensagem real (útil em produção se o erro
      // voltar a aparecer); UI mostra texto amigável.
      console.error(`[TaxonomyManagerModal] falha ao listar ${itemLabel}s:`, err);
      setError(`Não foi possível carregar a lista de ${itemLabel}s.`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    reload();
    // Reset estado do form ao abrir
    setNovoNome("");
    setNovoEscopo(ESCOPO_CATEGORIA.Despesa);
    setCreateError(null);
    setEditingId(null);
    setDeleteError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleCreate() {
    const nome = novoNome.trim();
    if (!nome) {
      setCreateError("Informe um nome.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const payload = hasEscopo ? { nome, escopo: Number(novoEscopo) } : { nome };
      const created = await onCreate(payload);
      setNovoNome("");
      setNovoEscopo(ESCOPO_CATEGORIA.Despesa);
      await reload();
      onItemCreated?.(created);
      onListChanged?.();
    } catch (err) {
      console.error(`[TaxonomyManagerModal] falha ao criar ${itemLabel}:`, err);
      setCreateError(
        err?.response?.data?.message ||
          `Erro ao criar ${itemLabel}.`
      );
    } finally {
      setCreating(false);
    }
  }

  function startEdit(item) {
    setEditingId(item.id);
    setEditingNome(item.nome);
    setEditingEscopo(item.escopo ?? ESCOPO_CATEGORIA.Despesa);
    setEditingError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingError(null);
  }

  async function saveEdit(item) {
    const nome = editingNome.trim();
    if (!nome) {
      setEditingError("Informe um nome.");
      return;
    }
    setSavingEdit(true);
    setEditingError(null);
    try {
      const payload = hasEscopo
        ? {
            nome,
            // Item de sistema: Escopo é imutável — preserva o valor original.
            escopo: item.isSistema ? item.escopo : Number(editingEscopo),
          }
        : { nome };
      await onUpdate(item.id, payload);
      setEditingId(null);
      await reload();
      onListChanged?.();
    } catch (err) {
      console.error(`[TaxonomyManagerModal] falha ao atualizar ${itemLabel}:`, err);
      setEditingError(
        err?.response?.data?.message ||
          `Erro ao atualizar ${itemLabel}.`
      );
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDelete() {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(toDelete.id);
      setToDelete(null);
      await reload();
      onListChanged?.();
    } catch (err) {
      // 400 (IsSistema — não deveria chegar aqui, lixeira já desabilitada)
      // 409 (em uso) — mostra mensagem amigável do servidor
      console.error(`[TaxonomyManagerModal] falha ao excluir ${itemLabel}:`, err);
      setDeleteError(
        err?.response?.data?.message ||
          `Erro ao excluir ${itemLabel}.`
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={titulo}
        subtitle="Crie, edite ou exclua itens. Os marcados com 🔒 são do sistema e não podem ser excluídos."
        width={620}
      >
        {/* ─── Seção: criar novo ─── */}
        <div
          style={{
            padding: "14px 16px",
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            marginBottom: 18,
          }}
        >
          <div style={labelStyle(C)}>+ Novo</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: hasEscopo ? "1fr 130px auto" : "1fr auto",
              gap: 8,
              alignItems: "stretch",
            }}
          >
            <input
              value={novoNome}
              onChange={(e) => setNovoNome(e.target.value)}
              placeholder="Nome"
              style={inputStyle(C)}
              maxLength={hasEscopo ? 60 : 50}
              disabled={creating}
            />
            {hasEscopo && (
              <select
                value={novoEscopo}
                onChange={(e) => setNovoEscopo(Number(e.target.value))}
                style={inputStyle(C)}
                disabled={creating}
              >
                <option value={ESCOPO_CATEGORIA.Despesa}>Despesa</option>
                <option value={ESCOPO_CATEGORIA.Receita}>Receita</option>
                <option value={ESCOPO_CATEGORIA.Ambas}>Ambas</option>
              </select>
            )}
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || !novoNome.trim()}
              style={{
                ...btnPrimary(C),
                opacity: creating || !novoNome.trim() ? 0.5 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {creating ? "..." : "+ Adicionar"}
            </button>
          </div>
          {createError && (
            <div style={errorBox(C)}>{createError}</div>
          )}
        </div>

        {/* ─── Seção: lista existente ─── */}
        {loading && (
          <div style={{ fontSize: 12, color: C.textMuted, padding: "8px 0" }}>
            Carregando...
          </div>
        )}
        {error && <div style={errorBox(C)}>{error}</div>}

        {!loading && items.length === 0 && !error && (
          <div
            style={{
              fontSize: 12,
              color: C.textMuted,
              fontStyle: "italic",
              padding: "12px 0",
            }}
          >
            Nenhum {itemLabel} cadastrado.
          </div>
        )}

        {items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map((item) => {
              const isEditing = editingId === item.id;
              return (
                <div
                  key={item.id}
                  style={{
                    padding: "10px 12px",
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {!isEditing && (
                    <>
                      <span
                        style={{
                          fontSize: 13,
                          color: item.isSistema ? C.textMuted : C.text,
                          fontWeight: 600,
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.isSistema ? "Item do sistema" : undefined}
                      >
                        {item.isSistema && "🔒 "}
                        {item.nome}
                      </span>
                      {hasEscopo && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "3px 8px",
                            borderRadius: 4,
                            background: C.bgCard,
                            color: C.textMuted,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ESCOPO_LABEL[item.escopo]}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        style={iconBtn(C)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteError(null);
                          setToDelete(item);
                        }}
                        disabled={item.isSistema}
                        title={
                          item.isSistema
                            ? `${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)} do sistema não pode ser excluída`
                            : "Excluir"
                        }
                        style={{
                          ...iconBtn(C),
                          color: item.isSistema ? C.textDim : C.red,
                          cursor: item.isSistema ? "not-allowed" : "pointer",
                          opacity: item.isSistema ? 0.4 : 1,
                        }}
                      >
                        🗑
                      </button>
                    </>
                  )}

                  {isEditing && (
                    <>
                      <input
                        value={editingNome}
                        onChange={(e) => setEditingNome(e.target.value)}
                        style={{ ...inputStyle(C), flex: 1 }}
                        maxLength={hasEscopo ? 60 : 50}
                        autoFocus
                      />
                      {hasEscopo && (
                        <select
                          value={editingEscopo}
                          onChange={(e) => setEditingEscopo(Number(e.target.value))}
                          disabled={item.isSistema}
                          title={
                            item.isSistema
                              ? "Escopo do sistema é imutável"
                              : undefined
                          }
                          style={{
                            ...inputStyle(C),
                            width: 130,
                            opacity: item.isSistema ? 0.5 : 1,
                            cursor: item.isSistema ? "not-allowed" : undefined,
                          }}
                        >
                          <option value={ESCOPO_CATEGORIA.Despesa}>Despesa</option>
                          <option value={ESCOPO_CATEGORIA.Receita}>Receita</option>
                          <option value={ESCOPO_CATEGORIA.Ambas}>Ambas</option>
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={() => saveEdit(item)}
                        disabled={savingEdit || !editingNome.trim()}
                        style={{ ...btnPrimary(C), padding: "6px 12px", fontSize: 11 }}
                      >
                        {savingEdit ? "..." : "Salvar"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={savingEdit}
                        style={{ ...btnSecondary(C), padding: "6px 10px", fontSize: 11 }}
                      >
                        Cancelar
                      </button>
                    </>
                  )}
                </div>
              );
            })}
            {editingError && <div style={errorBox(C)}>{editingError}</div>}
          </div>
        )}

        {deleteError && <div style={errorBox(C)}>{deleteError}</div>}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 18,
            paddingTop: 14,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <button type="button" onClick={onClose} style={btnSecondary(C)}>
            Fechar
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        title={`Excluir ${itemLabel}?`}
        message={`Remover "${toDelete?.nome}"? Lançamentos que usavam ${itemLabel === "categoria" ? "essa categoria" : "essa forma de pagamento"} perderão o vínculo, mas o registro histórico fica preservado.`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}

// ─── estilos compartilhados ───
function labelStyle(C) {
  return {
    fontSize: 10,
    fontWeight: 700,
    color: C.textMuted,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 8,
  };
}

function inputStyle(C) {
  return {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.inputBg || C.bgCardHover,
    color: C.text,
    fontSize: 12,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  };
}

function iconBtn(C) {
  return {
    padding: "6px 10px",
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: "transparent",
    color: C.textMuted,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
  };
}

function btnSecondary(C) {
  return {
    padding: "8px 16px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: "transparent",
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
  };
}

function btnPrimary(C) {
  return {
    padding: "9px 16px",
    borderRadius: 8,
    border: "none",
    background: C.accent,
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
  };
}

function errorBox(C) {
  return {
    marginTop: 10,
    padding: "8px 12px",
    background: C.redDim,
    color: C.red,
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
  };
}
